import InProgressBookingCart from '@/components/header/in-progress-booking-cart/InProgressBookingCart';
import RegularLanguageSelect from '@/components/header/language-select/RegularLanguageSelect';
import Logo from '@/components/header/Logo';
import { Link } from '@/components/primitives/Link';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { NavigationItem } from '@/types/navigation';
import { cn } from '@/utils/cn';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { getDisplayName } from '@/utils/profile';
import {
  Button,
  Divider,
  Icons,
  NavigationMenu,
  Popover,
  Typography,
} from '@packages/webshop-design-system';
import type { FC, ReactNode } from 'react';
import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type RegularNavMenuProps = {
  menuItems: Array<NavigationItem>;
  userMenuItems?: Array<[TransTextKeys, string, ReactNode]>;
};

const RegularNavMenu: FC<RegularNavMenuProps> = ({
  menuItems,
  userMenuItems,
}) => {
  const { isAuthenticated, logout } = useAuth();
  const profile = useSelector(profileDataSelector);
  const displayName = getDisplayName(profile);
  const location = useLocation();
  const navigate = useNavigate();
  const configuration = useTenantConfiguration();

  return (
    <div
      className="mx-auto flex w-full items-center justify-between"
      data-testid="navbar-regular"
    >
      <div className="flex items-center gap-6">
        <Logo />
        <NavigationMenu
          classNames={{ list: 'gap-4' }}
          items={menuItems.flatMap((item, index) => {
            const isActive = location.pathname.includes(item.path);
            const link = (
              <Link
                key={item.path}
                to={isActive ? location.pathname : item.path}
                active={isActive}
                onClick={item.onClick}
              >
                {item.name}
              </Link>
            );

            if (index < menuItems.length - 1) {
              return [
                link,
                <Divider
                  key={`divider-${item.path}`}
                  orientation="vertical"
                  className="h-4"
                  variant="strong"
                />,
              ];
            }

            return [link];
          })}
        />
      </div>
      <div className="flex items-center gap-4">
        <InProgressBookingCart />
        {isAuthenticated ? (
          <Popover
            trigger={
              <Button
                data-testid="my-account-button"
                variant="ghostLink"
                className="group flex gap-2"
              >
                <Icons.user />
                {displayName}
                <Icons.chevronDown
                  height={16}
                  width={16}
                  className="transition-transform duration-200 group-aria-expanded:rotate-180"
                />
              </Button>
            }
            content={
              <>
                {userMenuItems?.map(([labelKey, path, icon], key) => {
                  const isActive =
                    path !== 'logout' && location.pathname.includes(path);

                  return (
                    <Fragment key={key}>
                      {path === 'logout' && <Divider variant="regular" />}
                      <div
                        className={cn(
                          'flex h-10 w-full items-center justify-between px-3 py-2 text-dark hover:cursor-pointer hover:bg-primary-pale',
                          isActive && 'bg-primary-light'
                        )}
                        onClick={() =>
                          path === 'logout' ? logout() : navigate(path)
                        }
                        data-testid={`user-menu-${labelKey}`}
                      >
                        <div className="flex items-center">
                          <span className="mr-2 text-primary">{icon}</span>
                          <Typography variant="button">
                            <TransText i18nKey={labelKey} />
                          </Typography>
                        </div>
                        {isActive && (
                          <Icons.checkCircle
                            height={16}
                            width={16}
                            className="text-success-text"
                          />
                        )}
                      </div>
                    </Fragment>
                  );
                })}
              </>
            }
            placement="bottom end"
            data-testid="user-menu-popover"
            className={{ root: 'w-48', content: 'p-0' }}
          />
        ) : (
          (isLocalDevelopment() || configuration?.showLogin) && (
            <>
              <Link
                to="/login"
                data-testid="my-account-button"
                className="flex gap-2"
                active={location.pathname === '/login'}
              >
                <Icons.user />
                <TransText i18nKey="logIn" />
              </Link>
              <Divider
                orientation="vertical"
                className="h-4"
                variant="strong"
              />
            </>
          )
        )}
        <RegularLanguageSelect />
      </div>
    </div>
  );
};

export default RegularNavMenu;
